import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { X, Upload, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminPackForm({ pack, onClose }) {
  const [categories, setCategories] = useState(["Pretend Play"]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => {
      if (data.categories?.length) setCategories(data.categories);
    }).catch(() => {});
  }, []);
  const [form, setForm] = useState({
    title: pack?.title || "",
    description: pack?.description || "",
    age_range: pack?.age_range || "",
    category: pack?.category || "Pretend Play",
    price: pack?.price ?? 199,
    discounted_price: pack?.discounted_price ?? "",
    discount_label: pack?.discount_label || "",
    page_count: pack?.page_count ?? "",
    show_original_price: pack?.show_original_price ?? true,
    status: pack?.status || "draft",
  });
  const [existingImages, setExistingImages] = useState(pack?.images || []);
  const [newFiles, setNewFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const fileInputRef = useRef(null);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);

    try {
      let packId = pack?._id;
      const payload = {
        ...form,
        discounted_price:
          form.discounted_price === "" || form.discounted_price === null
            ? null
            : Number(form.discounted_price),
        page_count:
          form.page_count === "" || form.page_count === null
            ? null
            : Number(form.page_count),
      };

      if (packId) {
        await api.put(`/admin/packs/${packId}`, payload);
      } else {
        const { data } = await api.post("/admin/packs", payload);
        packId = data._id;
      }

      // Upload new images
      for (let i = 0; i < newFiles.length; i++) {
        setUploadProgress(`Uploading image ${i + 1} of ${newFiles.length}...`);
        const fd = new FormData();
        fd.append("file", newFiles[i]);
        await api.post(`/admin/packs/${packId}/images`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      toast.success(pack ? "Pack updated!" : "Pack created!");
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save pack");
    } finally {
      setSaving(false);
      setUploadProgress("");
    }
  }

  async function handleDeleteImage(imageId) {
    if (pack?._id) {
      try {
        await api.delete(`/admin/packs/${pack._id}/images/${imageId}`);
        toast.success("Image removed");
      } catch {
        toast.error("Failed to remove image");
        return;
      }
    }
    setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-cream overflow-y-auto"
      data-testid="admin-crud-form"
    >
      {/* Top bar */}
      <div className="sticky top-0 bg-white border-b border-border px-4 py-3 flex items-center justify-between z-10">
        <h2 className="font-heading font-bold text-lg">
          {pack ? "Edit Pack" : "New Pack"}
        </h2>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-9 w-9">
          <X className="w-5 h-5" />
        </Button>
      </div>

      <form
        onSubmit={handleSubmit}
        className="max-w-lg mx-auto p-4 pb-8 space-y-5"
      >
        {/* Title */}
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1.5 block">
            Title *
          </label>
          <Input
            value={form.title}
            onChange={(e) => updateField("title", e.target.value)}
            placeholder="e.g., Doctor Doctor Pretend Play Kit"
            className="rounded-xl"
            required
            data-testid="admin-pack-title-input"
          />
        </div>

        {/* Description */}
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1.5 block">
            Description
          </label>
          <Textarea
            value={form.description}
            onChange={(e) => updateField("description", e.target.value)}
            placeholder="One-line description of the pack"
            rows={3}
            className="rounded-xl resize-none"
          />
        </div>

        {/* Age Range + Price + Pages */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">
              Age Range
            </label>
            <Input
              value={form.age_range}
              onChange={(e) => updateField("age_range", e.target.value)}
              placeholder="e.g., 3-7 yrs"
              className="rounded-xl"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">
              Price (₹)
            </label>
            <Input
              type="number"
              value={form.price}
              onChange={(e) => updateField("price", Number(e.target.value))}
              className="rounded-xl"
              min={0}
              data-testid="admin-pack-price-input"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">
              Pages
            </label>
            <Input
              type="number"
              value={form.page_count}
              onChange={(e) =>
                updateField("page_count", e.target.value === "" ? "" : Number(e.target.value))
              }
              placeholder="e.g., 12"
              className="rounded-xl"
              min={1}
              data-testid="admin-pack-page-count-input"
            />
          </div>
        </div>

        {/* Discounted Price + Label */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">
              Discounted Price (₹)
            </label>
            <Input
              type="number"
              value={form.discounted_price}
              onChange={(e) =>
                updateField(
                  "discounted_price",
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
              placeholder="Leave empty if none"
              className="rounded-xl"
              min={0}
              data-testid="admin-pack-discounted-price-input"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">
              Discount Label
            </label>
            <Input
              value={form.discount_label}
              onChange={(e) => updateField("discount_label", e.target.value)}
              placeholder="e.g., Introductory Offer"
              className="rounded-xl"
              data-testid="admin-pack-discount-label-input"
            />
          </div>
        </div>

        {/* Category */}
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1.5 block">
            Category
          </label>
          <Select
            value={form.category}
            onValueChange={(v) => updateField("category", v)}
          >
            <SelectTrigger className="rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Price Display */}
        <div className="flex items-center justify-between bg-secondary/50 rounded-xl p-3">
          <div>
            <p className="text-sm font-semibold text-slate-700">Show original (struck-through) price</p>
            <p className="text-xs text-slate-400">When a discounted price is set</p>
          </div>
          <Switch checked={form.show_original_price} onCheckedChange={(v) => updateField("show_original_price", v)} />
        </div>

        {/* Status */}
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1.5 block">
            Status
          </label>
          <Select
            value={form.status}
            onValueChange={(v) => updateField("status", v)}
            data-testid="admin-pack-status-select"
          >
            <SelectTrigger className="rounded-xl" data-testid="admin-pack-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="coming_soon">Coming Soon</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Preview Images */}
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-2 block">
            Full PDF (what buyers receive)
          </label>
          {pack?.pdf_path ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-3 flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-green-800 truncate">{pack.pdf_filename || "pack.pdf"}</p>
                <p className="text-xs text-green-600">{pack.pdf_size ? `${(pack.pdf_size / 1024 / 1024).toFixed(1)} MB` : ""}</p>
              </div>
              <a href={`${API_URL}/api/admin/packs/${pack._id}/pdf/download`} target="_blank" rel="noopener noreferrer">
                <Button type="button" variant="outline" size="sm" className="rounded-full text-xs h-7 shrink-0">
                  Open / Download
                </Button>
              </a>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
              <p className="text-xs text-amber-700 font-semibold">No PDF uploaded. Buy button will be hidden until a PDF is added.</p>
            </div>
          )}
          <Button
            type="button" variant="outline"
            className="w-full rounded-xl border-dashed border-2 h-12 mb-3"
            onClick={() => {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = "application/pdf";
              input.onchange = async (ev) => {
                const f = ev.target.files?.[0];
                if (!f) return;
                if (f.size > 200 * 1024 * 1024) { toast.error("PDF too large. Max 200MB"); return; }
                if (!pack?._id) { toast.error("Save the pack first, then upload the PDF"); return; }
                const fd = new FormData();
                fd.append("file", f);
                try {
                  await api.post(`/admin/packs/${pack._id}/pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                  toast.success("PDF uploaded!");
                  onClose();
                } catch (err) { toast.error(err.response?.data?.detail || "PDF upload failed"); }
              };
              input.click();
            }}
            data-testid="admin-pack-pdf-upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" /> {pack?.pdf_path ? "Replace PDF" : "Upload PDF"}
          </Button>
        </div>

        {/* Preview Images */}
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-2 block">
            Preview Images (catalog thumbnails)
          </label>

          <div className="grid grid-cols-3 gap-2 mb-3">
            {existingImages.map((img) => (
              <div
                key={img.id}
                className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 group"
              >
                <img
                  src={`${API_URL}/api/images/${img.storage_path}`}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => handleDeleteImage(img.id)}
                  className="absolute top-1 right-1 bg-red-500/90 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            {newFiles.map((file, i) => (
              <div
                key={`new-${i}`}
                className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 group"
              >
                <img
                  src={URL.createObjectURL(file)}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() =>
                    setNewFiles((prev) => prev.filter((_, j) => j !== i))
                  }
                  className="absolute top-1 right-1 bg-red-500/90 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full rounded-xl border-dashed border-2 h-12"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-4 h-4 mr-2" /> Add Images
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              setNewFiles((prev) => [...prev, ...files]);
              e.target.value = "";
            }}
          />
          <p className="text-xs text-slate-400 mt-1.5">
            JPEG, PNG, WebP, GIF. Max 5MB each. Images auto-compressed.
          </p>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          className="w-full bg-coral hover:bg-coral-hover text-white rounded-full h-12 text-base font-bold"
          disabled={saving}
          data-testid="admin-pack-save-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {uploadProgress || "Saving..."}
            </>
          ) : pack ? (
            "Update Pack"
          ) : (
            "Create Pack"
          )}
        </Button>
      </form>
    </div>
  );
}
